import { useTranslation } from "react-i18next";

import { CampaignPeriod, SimCardDetail } from "@/services/model";
import { starterKit } from "@/utils/constants";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import { getBaseFlatName, getTariffName } from "@/utils/deviceUtils";
import { calculateDiscounts } from "@/utils/tariffUtils";

/**
 * The line items behind "Subscription base price": base price, discounts (with their
 * campaign period, e.g. "until revoke"), and add-ons (Watch SIM, Voice).
 * Shared by the persistent price card and the auto-renewal confirmation,
 * which both draw the same `dire-table` breakdown.
 */
export const useSubscriptionPriceBreakdown = (
  simDetail: SimCardDetail | undefined,
) => {
  const { t, i18n } = useTranslation(["portal", "translation"]);

  const basePrice = simDetail?.priceBase ?? 0;

  // The starter kit is a one-off and neither listed nor deducted here.
  const allDiscounts = calculateDiscounts(simDetail?.localDiscounts);
  const discounts = allDiscounts.filter(
    ({ discountName, campaignPeriod }) =>
      discountName && campaignPeriod && discountName !== starterKit,
  );
  const starterKitAmount =
    allDiscounts.find(({ discountName }) => discountName === starterKit)
      ?.amount ?? 0;
  const totalPrice = (simDetail?.priceDiscounted ?? 0) + starterKitAmount;

  const addOns = [
    simDetail?.priceWatch
      ? {
          label: t("translation:Common.products.watchSim"),
          price: simDetail.priceWatch,
        }
      : undefined,
    simDetail?.priceVoice
      ? {
          label: t("cockpit.managementTile.watchSim.display.voiceOption"),
          price: simDetail.priceVoice,
        }
      : undefined,
  ].filter((addOn): addOn is { label: string; price: number } => !!addOn);

  // The campaign period goes on its own line beneath the discount name.
  const getDiscountPeriod = (campaignPeriod: CampaignPeriod) => {
    switch (campaignPeriod.campaignPeriodEnum) {
      case "one_time":
        return undefined;
      // Without an end date there is nothing to name, and formatting the
      // missing one would print "(until Invalid Date)" into the price table.
      case "date_specific":
        return campaignPeriod.endDate
          ? `(${t("cockpit.managementTile.tariff.until")} ${getLocalizedDateString(campaignPeriod.endDate, i18n.language)})`
          : undefined;
      default:
        return `(${t("cockpit.managementTile.tariff.untilRevoke")})`;
    }
  };

  // Without discounts or add-ons there is nothing left to sum up, so the
  // single row carries the billing interval itself.
  const isSingleRow = discounts.length === 0 && addOns.length === 0;
  const productName = simDetail
    ? getBaseFlatName(simDetail) || getTariffName(simDetail)
    : "";

  return {
    basePrice,
    discounts,
    addOns,
    totalPrice,
    isSingleRow,
    productName,
    getDiscountPeriod,
  };
};
